import { Texture, SRGBColorSpace, MeshStandardMaterial } from 'three';

const TEXTURE_SIZE = 512;

// SVG data from the provided SVG
const SVG_VIEWBOX_WIDTH = 20;
const SVG_VIEWBOX_HEIGHT = 20;
const SVG_CIRCLE_CX = 9.82812;
const SVG_CIRCLE_CY = 9.82812;
const SVG_CIRCLE_R = 8.77562;
const SVG_CIRCLE_STROKE_WIDTH = 2.10501;

const SVG_PATHS = [
  'M10.8645 6.5892C10.6208 6.50264 10.4083 6.34576 10.2538 6.13842C10.0993 5.93107 10.0097 5.68256 9.99645 5.42431C9.98317 5.16606 10.0468 4.90968 10.1792 4.68757C10.3116 4.46547 10.507 4.28763 10.7405 4.17654C10.974 4.06545 11.2352 4.02609 11.4911 4.06345C11.7469 4.10081 11.986 4.21321 12.178 4.38643C12.37 4.55965 12.5063 4.78592 12.5697 5.03661C12.6331 5.28731 12.6207 5.55117 12.5342 5.79484C12.4181 6.12155 12.1769 6.38876 11.8638 6.53772C11.5507 6.68668 11.1913 6.7052 10.8645 6.5892Z',
  'M10.7265 7.84209C10.9011 8.5284 10.8859 9.24923 10.6825 9.92757C10.4792 10.6059 10.0954 11.2163 9.57216 11.6934C9.0489 12.1706 8.40582 12.4966 7.71166 12.6366C7.01749 12.7767 6.29831 12.7255 5.63097 12.4886L5.61836 12.4841L4.91273 14.4705L4.92534 14.4749C8.05623 15.5872 11.4957 13.9506 12.6079 10.82C12.9508 9.85931 13.0425 8.82697 12.8743 7.82092L10.7265 7.84209Z',
];

const COIN_COLORS = {
  background: '#FFF0C2',
  stroke: '#FDBF01',
  path: '#FDBF01',
  side: '#fe9f0a',
};

class CoinTextureManager {
  constructor() {
    this.textureCache = new Map();
    this.sideMaterialCache = null;
  }

  getCanvasSize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    return Math.round(TEXTURE_SIZE * dpr);
  }

  createCanvas() {
    const size = this.getCanvasSize();
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    return { canvas, size, ctx: canvas.getContext('2d') };
  }

  createTextureFromCanvas(canvas) {
    const texture = new Texture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.needsUpdate = true;
    return texture;
  }

  drawCoinFaceTexture(ctx, size) {
    const cx = size / 2;
    const cy = size / 2;

    // Fill background with solid color (edge to edge)
    ctx.fillStyle = COIN_COLORS.stroke;
    ctx.fillRect(0, 0, size, size);

    // Calculate scale from SVG viewbox to canvas size (edge to edge)
    const svgScale = size / SVG_VIEWBOX_WIDTH;

    // Translate to center and scale
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(svgScale, svgScale);
    ctx.translate(-SVG_VIEWBOX_WIDTH / 2, -SVG_VIEWBOX_HEIGHT / 2);

    // Draw circle
    ctx.beginPath();
    ctx.arc(SVG_CIRCLE_CX, SVG_CIRCLE_CY, SVG_CIRCLE_R, 0, Math.PI * 2);
    ctx.fillStyle = COIN_COLORS.background;
    ctx.fill();
    ctx.strokeStyle = COIN_COLORS.stroke;
    ctx.lineWidth = SVG_CIRCLE_STROKE_WIDTH;
    ctx.stroke();

    // Draw paths (the "J" shape)
    ctx.fillStyle = COIN_COLORS.path;
    SVG_PATHS.forEach((pathData) => {
      const path = new Path2D(pathData);
      ctx.fill(path);
    });

    ctx.restore();
  }

  getCoinFaceTexture() {
    const cacheKey = 'toast-coin-face';

    if (this.textureCache.has(cacheKey)) {
      return this.textureCache.get(cacheKey);
    }

    const { canvas, size, ctx } = this.createCanvas();
    this.drawCoinFaceTexture(ctx, size);

    const texture = this.createTextureFromCanvas(canvas);
    this.textureCache.set(cacheKey, texture);
    return texture;
  }

  getCoinSideMaterial() {
    if (this.sideMaterialCache) {
      return this.sideMaterialCache;
    }

    this.sideMaterialCache = new MeshStandardMaterial({
      color: COIN_COLORS.side,
    });

    return this.sideMaterialCache;
  }

  getCoinMaterials() {
    const sideMaterial = this.getCoinSideMaterial();
    const faceTexture = this.getCoinFaceTexture();

    const topMaterial = new MeshStandardMaterial({ map: faceTexture });
    const bottomMaterial = new MeshStandardMaterial({ map: faceTexture });

    return [sideMaterial, topMaterial, bottomMaterial];
  }
}

export default CoinTextureManager;

